// src/admin/Dashboard.jsx
import React from "react";
import { Row, Col, Card, Table, Tag, Progress } from "antd";
import {
  CarOutlined, UserOutlined, TeamOutlined,
  CheckCircleOutlined, ClockCircleOutlined,
  RiseOutlined, DollarOutlined,
} from "@ant-design/icons";
import { INITIAL_BOOKINGS, INITIAL_CABS, INITIAL_DRIVERS, INITIAL_USERS } from "../data/store";

const Dashboard = () => {
  const totalRevenue = INITIAL_BOOKINGS
    .filter(b => b.status === "Completed")
    .reduce((sum, b) => sum + b.price, 0);

  const stats = [
    {
      title:   "Total Bookings",
      value:   INITIAL_BOOKINGS.length,
      icon:    <CheckCircleOutlined style={{ fontSize:28, color:"#f5c518" }} />,
      bg:      "linear-gradient(135deg,#fffbea,#fff9d0)",
      border:  "#f5c518",
      sub:     `${INITIAL_BOOKINGS.filter(b=>b.status==="Pending").length} pending`,
    },
    {
      title:   "Active Drivers",
      value:   INITIAL_DRIVERS.filter(d=>d.status==="Active").length,
      icon:    <UserOutlined style={{ fontSize:28, color:"#3b82f6" }} />,
      bg:      "linear-gradient(135deg,#eff6ff,#dbeafe)",
      border:  "#3b82f6",
      sub:     `${INITIAL_DRIVERS.length} total drivers`,
    },
    {
      title:   "Total Cabs",
      value:   INITIAL_CABS.length,
      icon:    <CarOutlined style={{ fontSize:28, color:"#22c55e" }} />,
      bg:      "linear-gradient(135deg,#f0fdf4,#dcfce7)",
      border:  "#22c55e",
      sub:     `${INITIAL_CABS.filter(c=>c.status==="Available").length} available`,
    },
    {
      title:   "Registered Users",
      value:   INITIAL_USERS.length,
      icon:    <TeamOutlined style={{ fontSize:28, color:"#a855f7" }} />,
      bg:      "linear-gradient(135deg,#faf5ff,#ede9fe)",
      border:  "#a855f7",
      sub:     `${INITIAL_USERS.filter(u=>u.status==="Active").length} active`,
    },
    {
      title:   "Revenue (Completed)",
      value:   `₹${totalRevenue.toLocaleString()}`,
      icon:    <DollarOutlined style={{ fontSize:28, color:"#f97316" }} />,
      bg:      "linear-gradient(135deg,#fff7ed,#ffedd5)",
      border:  "#f97316",
      sub:     `${INITIAL_BOOKINGS.filter(b=>b.status==="Completed").length} completed rides`,
    },
    {
      title:   "Ongoing Rides",
      value:   INITIAL_BOOKINGS.filter(b=>b.status==="Ongoing").length,
      icon:    <ClockCircleOutlined style={{ fontSize:28, color:"#06b6d4" }} />,
      bg:      "linear-gradient(135deg,#ecfeff,#cffafe)",
      border:  "#06b6d4",
      sub:     "Live right now",
    },
  ];

  // Booking status breakdown
  const statusCounts = ["Pending","Approved","Ongoing","Completed","Cancelled"].map(s=>({
    status: s,
    count:  INITIAL_BOOKINGS.filter(b=>b.status===s).length,
  }));
  const statusColors = { Pending:"#f97316", Approved:"#3b82f6", Ongoing:"#06b6d4", Completed:"#22c55e", Cancelled:"#ef4444" };

  // Recent bookings table
  const recentBookings = [...INITIAL_BOOKINGS]
    .sort((a,b)=>b.key-a.key)
    .slice(0,5);

  const recentCols = [
    { title:"User",   dataIndex:"user",  key:"user" },
    { title:"Route",  dataIndex:"route", key:"route" },
    { title:"Driver", dataIndex:"driver",key:"driver" },
    { title:"Fare",   dataIndex:"price", key:"price", render:p=><b style={{color:"#d4a80f"}}>₹{p.toLocaleString()}</b> },
    {
      title:"Status", dataIndex:"status", key:"status",
      render: s => {
        const map = { Pending:"orange", Approved:"blue", Ongoing:"cyan", Completed:"green", Cancelled:"red" };
        return <Tag color={map[s]||"default"}>{s}</Tag>;
      }
    },
  ];

  // Top drivers
  const topDrivers = [...INITIAL_DRIVERS].sort((a,b)=>b.rating-a.rating).slice(0,4);

  return (
    <div style={{ fontFamily:"'Plus Jakarta Sans',sans-serif" }}>
      <div style={{ marginBottom:24 }}>
        <h2 style={{ fontSize:24, fontWeight:800, color:"#1a1a2e", margin:0 }}>Dashboard</h2>
        <p style={{ color:"#9ca3af", marginTop:4, fontSize:13 }}>Welcome back! Here's what's happening with HaloSorath today.</p>
      </div>

      {/* ── Stat cards ── */}
      <Row gutter={[16,16]} style={{ marginBottom:24 }}>
        {stats.map((s,i) => (
          <Col xs={24} sm={12} lg={8} key={i}>
            <Card
              style={{ background:s.bg, border:`1.5px solid ${s.border}33`, borderRadius:18, boxShadow:"0 4px 16px rgba(0,0,0,.06)" }}
              bodyStyle={{ padding:"20px 22px" }}
            >
              <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start" }}>
                <div>
                  <p style={{ fontSize:12, fontWeight:700, color:"#6b7280", textTransform:"uppercase", letterSpacing:"1px", marginBottom:8 }}>{s.title}</p>
                  <p style={{ fontSize:32, fontWeight:900, color:"#1a1a2e", margin:0, lineHeight:1 }}>{s.value}</p>
                  <p style={{ fontSize:12, color:"#9ca3af", marginTop:6 }}>{s.sub}</p>
                </div>
                <div style={{ width:52, height:52, borderRadius:14, background:"rgba(255,255,255,.7)", display:"flex", alignItems:"center", justifyContent:"center", boxShadow:"0 2px 8px rgba(0,0,0,.06)" }}>
                  {s.icon}
                </div>
              </div>
            </Card>
          </Col>
        ))}
      </Row>

      <Row gutter={[16,16]} style={{ marginBottom:24 }}>
        {/* ── Booking status breakdown ── */}
        <Col xs={24} lg={10}>
          <Card
            title={<span style={{ fontWeight:700, color:"#1a1a2e" }}>📊 Booking Status</span>}
            style={{ borderRadius:18, border:"1px solid #e5e7eb" }}
            bodyStyle={{ padding:"20px 22px" }}
          >
            {statusCounts.map(({status,count}) => (
              <div key={status} style={{ marginBottom:14 }}>
                <div style={{ display:"flex", justifyContent:"space-between", marginBottom:5 }}>
                  <span style={{ fontSize:13, fontWeight:600, color:"#374151" }}>{status}</span>
                  <span style={{ fontSize:13, fontWeight:700, color:statusColors[status] }}>{count}</span>
                </div>
                <Progress
                  percent={INITIAL_BOOKINGS.length ? Math.round((count/INITIAL_BOOKINGS.length)*100) : 0}
                  showInfo={false}
                  strokeColor={statusColors[status]}
                  trailColor="#f3f4f6"
                  strokeWidth={8}
                />
              </div>
            ))}
          </Card>
        </Col>

        {/* ── Top Drivers ── */}
        <Col xs={24} lg={14}>
          <Card
            title={<span style={{ fontWeight:700, color:"#1a1a2e" }}>⭐ Top Rated Drivers</span>}
            style={{ borderRadius:18, border:"1px solid #e5e7eb" }}
            bodyStyle={{ padding:"16px 22px" }}
          >
            {topDrivers.map((d,i) => (
              <div key={d.key} style={{ display:"flex", alignItems:"center", justifyContent:"space-between", padding:"10px 0", borderBottom: i<topDrivers.length-1?"1px solid #f3f4f6":"none" }}>
                <div style={{ display:"flex", alignItems:"center", gap:12 }}>
                  <div style={{ width:38, height:38, borderRadius:"50%", background:"linear-gradient(135deg,#f5c518,#d4a80f)", display:"flex", alignItems:"center", justifyContent:"center", fontWeight:800, color:"#1a1a2e", fontSize:14 }}>{d.name[0]}</div>
                  <div>
                    <div style={{ fontWeight:700, fontSize:14, color:"#1a1a2e" }}>{d.name}</div>
                    <div style={{ fontSize:12, color:"#9ca3af" }}>{d.car} · {d.trips.toLocaleString()} trips</div>
                  </div>
                </div>
                <div style={{ display:"flex", alignItems:"center", gap:8 }}>
                  <Tag color={d.status==="Active"?"green":"red"}>{d.status}</Tag>
                  <span style={{ fontWeight:800, color:"#f5c518", fontSize:15 }}>★ {d.rating}</span>
                </div>
              </div>
            ))}
          </Card>
        </Col>
      </Row>

      {/* ── Recent Bookings ── */}
      <Card
        title={<span style={{ fontWeight:700, color:"#1a1a2e" }}>🕐 Recent Bookings</span>}
        style={{ borderRadius:18, border:"1px solid #e5e7eb" }}
        bodyStyle={{ padding:0 }}
      >
        <Table
          dataSource={recentBookings}
          columns={recentCols}
          pagination={false}
          size="middle"
          scroll={{ x: true }}
          style={{ borderRadius:18 }}
        />
      </Card>
    </div>
  );
};

export default Dashboard;